// =============================================================================
// Cookie Notice Component - Essential Cookies Only (Current Version)
// =============================================================================
// Purpose: Inform users about essential authentication cookies
// Legal: No consent required for essential cookies (DSGVO Art. 6 Abs. 1 lit. b)
// NOTE: This component has embedded translations to work independently of i18next
// =============================================================================
import React, { useState, useEffect } from 'react'

// =============================================================================
// NAMESPACED LOCALSTORAGE - Prevents collisions with other scripts
// =============================================================================
const NAMESPACE = 'blog-feilers-cookie'

const storage = {
  get: (key) => localStorage.getItem(`${NAMESPACE}-${key}`),
  set: (key, value) => localStorage.setItem(`${NAMESPACE}-${key}`, value),
  remove: (key) => localStorage.removeItem(`${NAMESPACE}-${key}`),
  clear: () => {
    Object.keys(localStorage)
      .filter(key => key.startsWith(NAMESPACE))
      .forEach(key => localStorage.removeItem(key))
  }
}

// Embedded translations - works without i18next context
const TRANSLATIONS = {
  de: {
    title: 'Cookie-Hinweis',
    description: 'Diese Website verwendet ausschließlich technisch notwendige Cookies für die Authentifizierung und grundlegende Funktionalität. Es werden keine Tracking- oder Analytics-Cookies verwendet.',
    legalInfo: 'Weitere Informationen finden Sie in unserer Datenschutzerklärung.',
    acknowledge: 'Verstanden',
    duration: 'Zustimmung gültig für:',
    days: 'Tage',
    consentInfo: 'Aktuelle Zustimmung:',
    consentDate: 'Zugestimmt am:',
    expiresDate: 'Gültig bis:',
    expired: 'Abgelaufen am:',
    changeSettings: 'Einstellungen ändern',
    preview: 'Vorschau:'
  },
  en: {
    title: 'Cookie Notice',
    description: 'This website uses only technically necessary cookies for authentication and basic functionality. No tracking or analytics cookies are used.',
    legalInfo: 'For more information, please see our privacy policy.',
    acknowledge: 'Got it',
    duration: 'Consent valid for:',
    days: 'days',
    consentInfo: 'Current consent:',
    consentDate: 'Consented on:',
    expiresDate: 'Valid until:',
    expired: 'Expired on:',
    changeSettings: 'Change settings',
    preview: 'Preview:'
  }
}

// Duration options (in days)
const DURATION_OPTIONS = [1, 5, 15, 30, 90, 180, 365]

const CookieNotice = () => {
  const [show, setShow] = useState(false)
  const [language, setLanguage] = useState('de')
  const [duration, setDuration] = useState(30)
  const [existingConsent, setExistingConsent] = useState(null)

  useEffect(() => {
    if (typeof window === 'undefined') return

    // Detect language from HTML lang attribute
    const htmlLang = document.documentElement.lang || 'de'
    const detectedLang = htmlLang.startsWith('en') ? 'en' : 'de'
    setLanguage(detectedLang)

    // Check for force-show flag from footer link
    const forceShow = sessionStorage.getItem(`${NAMESPACE}-force-show`)
    if (forceShow) {
      sessionStorage.removeItem(`${NAMESPACE}-force-show`)
    }

    // Check if user has already acknowledged the notice (namespaced)
    const acknowledged = storage.get('acknowledged')
    const consentDate = storage.get('date')
    const consentDuration = storage.get('duration')

    // Load previous duration setting
    if (consentDuration) {
      setDuration(parseInt(consentDuration))
    }

    if (acknowledged && consentDate && consentDuration) {
      // Check if consent is still valid
      const expiryDate = new Date(consentDate)
      expiryDate.setDate(expiryDate.getDate() + parseInt(consentDuration))

      const now = new Date()
      const isExpired = now > expiryDate

      // Prepare consent data for display
      const consentData = {
        date: consentDate,
        duration: consentDuration,
        expiryDate: expiryDate.toISOString(),
        isExpired: isExpired,
        daysRemaining: isExpired ? 0 : Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24))
      }

      setExistingConsent(consentData)

      // Show banner if:
      // 1. Consent expired, OR
      // 2. Force-show flag is set (from footer link)
      if (isExpired || forceShow) {
        setShow(true)
      }
    } else if (!acknowledged) {
      // No consent yet - show banner
      setShow(true)
    }
  }, [])

  const handleAcknowledge = () => {
    const now = new Date().toISOString()
    const currentDuration = storage.get('duration')
    const currentDate = storage.get('date')

    // Check if duration changed OR previous consent had expired.
    // Both cases mean: this is a genuinely NEW consent decision, so the
    // start date (and therefore the expiry date) needs to be reset to "now".
    const durationChanged =
      currentDuration !== duration.toString() ||
      (existingConsent && existingConsent.isExpired)

    storage.set('acknowledged', 'true')

    // Only update the stored date if:
    // 1. No old date exists (first consent), OR
    // 2. Duration was changed, OR
    // 3. Old consent had expired
    if (!currentDate || durationChanged) {
      storage.set('date', now)
      console.log('📅 New expiry date set (duration changed, expired, or first consent)')
    } else {
      console.log('📅 Keeping old expiry date (no change)')
    }

    storage.set('duration', duration.toString())

    setShow(false)

    // Use the SAME start date for the UI update that was just used for storage,
    // so the banner never shows something different from what's actually saved.
    const startDateForDisplay = (!currentDate || durationChanged) ? now : currentDate
    const expiryDate = new Date(startDateForDisplay)
    expiryDate.setDate(expiryDate.getDate() + duration)

    setExistingConsent({
      date: startDateForDisplay,
      duration: duration.toString(),
      expiryDate: expiryDate.toISOString(),
      isExpired: false,
      daysRemaining: duration
    })
  }

  if (!show) return null

  const t = TRANSLATIONS[language] || TRANSLATIONS.de

  // Format dates for display
  const formatDate = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  return (
    <div className="is-fixed-bottom" style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: 1000,
      margin: 0
    }}>
      <div className="notification is-info" style={{
        margin: 0,
        borderRadius: 0,
        paddingTop: '1.5rem',
        paddingBottom: '1.5rem'
      }}>
        <div className="container">
          <button
            className="delete is-medium"
            onClick={handleAcknowledge}
            aria-label="close"
            style={{ position: 'absolute', top: '1rem', right: '1rem' }}
          ></button>

          <div className="content">
            <h4 className="title is-5 mb-3">
              <span className="icon-text">
                <span className="icon">
                  <i className="fas fa-cookie-bite"></i>
                </span>
                <span>{t.title}</span>
              </span>
            </h4>
            <p className="mb-2">{t.description}</p>
            <p className="is-size-7 has-text-grey-dark mb-3">{t.legalInfo}</p>

            {/* Display existing consent info OR preview for first consent */}
            {existingConsent ? (
              // Show actual consent info
              <div className={`notification ${existingConsent.isExpired ? 'is-warning' : 'is-success'} is-light mb-4`}>
                <div className="content is-size-7 mb-0">
                  <strong className="is-size-6">
                    <span className="icon-text">
                      <span className="icon">
                        <i className={`fas ${existingConsent.isExpired ? 'fa-exclamation-triangle' : 'fa-check-circle'}`}></i>
                      </span>
                      <span>{t.consentInfo}</span>
                    </span>
                  </strong>

                  <div className="mt-2">
                    <div className="columns is-mobile is-multiline is-variable is-1">
                      <div className="column is-half-mobile is-one-third-tablet">
                        <strong>{t.consentDate}</strong>
                        <br />
                        {formatDate(existingConsent.date)}
                      </div>

                      <div className="column is-half-mobile is-one-third-tablet">
                        <strong>{existingConsent.isExpired ? t.expired : t.expiresDate}</strong>
                        <br />
                        {formatDate(existingConsent.expiryDate)}
                      </div>

                      {!existingConsent.isExpired && existingConsent.daysRemaining && (
                        <div className="column is-half-mobile is-one-third-tablet">
                          <strong>{t.duration}</strong>
                          <br />
                          {existingConsent.daysRemaining} {t.days}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              // Show preview for first consent
              <div className="notification is-info is-light mb-4">
                <div className="content is-size-7 mb-0">
                  <strong className="is-size-6">
                    <span className="icon-text">
                      <span className="icon">
                        <i className="fas fa-info-circle"></i>
                      </span>
                      <span>{t.preview}</span>
                    </span>
                  </strong>

                  <div className="mt-2">
                    <div className="columns is-mobile is-multiline is-variable is-1">
                      <div className="column is-half-mobile is-one-third-tablet">
                        <strong>{t.consentDate}</strong>
                        <br />
                        {formatDate(new Date().toISOString())}
                      </div>

                      <div className="column is-half-mobile is-one-third-tablet">
                        <strong>{t.expiresDate}</strong>
                        <br />
                        {(() => {
                          const expiry = new Date()
                          expiry.setDate(expiry.getDate() + duration)
                          return formatDate(expiry.toISOString())
                        })()}
                      </div>

                      <div className="column is-half-mobile is-one-third-tablet">
                        <strong>{t.duration}</strong>
                        <br />
                        {duration} {t.days}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Duration Selector */}
            <div className="field">
              <label className="label is-small">{t.duration}</label>
              <div className="control">
                <div className="buttons has-addons">
                  {DURATION_OPTIONS.map((days) => (
                    <button
                      key={days}
                      className={`button is-small ${duration === days ? 'is-primary is-selected' : 'is-light'}`}
                      onClick={() => setDuration(days)}
                    >
                      {days} {t.days}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Acknowledge Button */}
            <div className="field">
              <div className="control">
                <button className="button is-primary is-medium" onClick={handleAcknowledge}>
                  <span className="icon">
                    <i className="fas fa-check"></i>
                  </span>
                  <span>{t.acknowledge}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Export function to manually trigger cookie notice (for footer link)
export const showCookieNotice = () => {
  if (typeof window === 'undefined') return

  // Set a temporary flag instead of deleting stored consent data,
  // so previously saved date/duration survive the reload and can
  // still be displayed in the banner.
  sessionStorage.setItem(`${NAMESPACE}-force-show`, 'true')

  // Reload page to trigger the banner
  window.location.reload()
}

export default CookieNotice